import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Tag, Space, Button, Form, Input, Select, Drawer, message, Checkbox, Modal, Radio, Pagination, Typography, Dropdown } from 'antd';
import dayjs from 'dayjs';
import QRCode from 'qrcode';
import { useLocation, useNavigate } from 'react-router-dom';
import { MoreOutlined } from '@ant-design/icons';
import { listDebtNoticeBatches, getDebtNoticeBatchStatus, getDebtNoticeInternalDownloadUrl, resendDebtNoticeBatch, sendDebtNoticeCode, sendDebtNoticeResendCode, createNoticeLetterShare } from '../../services/debt';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './NoticeHistoryPage.css';

const { Text } = Typography;

const statusOptions = [
  { label: '全部', value: '' },
  { label: '发送中', value: 'SENDING' },
  { label: '部分成功', value: 'PARTIAL_SUCCESS' },
  { label: '成功', value: 'SUCCESS' },
  { label: '失败', value: 'FAILED' },
  { label: '已取消', value: 'CANCELLED' },
];

const ActionCell = ({ record, layoutKey, actionWidth, onOpenDetail, onOpenResend, onOpenWechatShare }) => {
  const containerRef = useRef(null);

  const candidates = useMemo(() => ([
    { key: 'detail', label: '发送明细' },
    { key: 'resend', label: '重发催告' },
    { key: 'wechatShare', label: '微信分享' },
  ]), []);

  const [inlineKeys, setInlineKeys] = useState(() => candidates.map(a => a.key));

  useEffect(() => {
    setInlineKeys(candidates.map(a => a.key));
  }, [candidates, layoutKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const isOverflow = el.scrollWidth > el.clientWidth + 1;
    if (isOverflow && inlineKeys.length > 1) {
      const next = inlineKeys.slice(0, -1);
      if (next.join('|') !== inlineKeys.join('|')) {
        setInlineKeys(next);
      }
    }
  }, [candidates, inlineKeys]);

  const inlineActions = candidates.filter(a => inlineKeys.includes(a.key));
  const dropdownActions = candidates.filter(a => !inlineKeys.includes(a.key));

  const handleAction = async (key) => {
    if (key === 'detail') return onOpenDetail?.(record);
    if (key === 'resend') return onOpenResend?.(record);
    if (key === 'wechatShare') return onOpenWechatShare?.(record);
  };

  return (
    <div
      ref={containerRef}
      className="c-actions-inline c-actions-cell"
      style={{ '--actions-cell-width': actionWidth }}
    >
      {inlineActions.map(action => (
        <Button
          key={action.key}
          type="link"
          className="c-btn-link-compact"
          onClick={() => handleAction(action.key)}
        >
          {action.label}
        </Button>
      ))}

      {dropdownActions.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: dropdownActions.map(a => ({ key: a.key, label: a.label })),
            onClick: async ({ key }) => {
              await handleAction(key);
            },
          }}
        >
          <Button type="link" className="c-btn-link-compact c-actions-more" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      )}
    </div>
  );
};

const NoticeHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendBatch, setResendBatch] = useState(null);
  const [resendSending, setResendSending] = useState(false);
  const [resendSms, setResendSms] = useState(true);
  const [resendEmail, setResendEmail] = useState(false);
  const [resendCode, setResendCode] = useState('');
  const [resendSendingCode, setResendSendingCode] = useState(false);
  const [sharePickOpen, setSharePickOpen] = useState(false);
  const [sharePickLoading, setSharePickLoading] = useState(false);
  const [sharePickBatchId, setSharePickBatchId] = useState(null);
  const [sharePickRecipients, setSharePickRecipients] = useState([]);
  const [sharePickRecipientId, setSharePickRecipientId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareQrUrl, setShareQrUrl] = useState('');
  const skipNextAutoFetchRef = useRef(false);

  const urlFilters = useMemo(() => {
    // 支持从 URL 传入筛选条件：
    // - debtId/debt_id：用于限制只看某一笔债权的催告批次
    // - debtNo/debt_no：用于“首次进入”时预填 keyword，便于从债权详情/列表跳转定位批次
    const sp = new URLSearchParams(location.search || '');
    const debtId = (sp.get('debtId') || sp.get('debt_id') || '').trim();
    const debtNo = (sp.get('debtNo') || sp.get('debt_no') || '').trim();
    return {
      debtId: debtId ? Number(debtId) : null,
      debtNo: debtNo || '',
    };
  }, [location.search]);

  const fetchList = useCallback(async (pageCfg, overrideValues) => {
    try {
      setLoading(true);
      // overrideValues 用于控制“首次进入时用 URL 预填的 keyword 立即查询”
      // 其他场景默认从表单取值，避免出现“用户清空 keyword 但仍被 URL debtNo 兜底”导致无法清空筛选
      const values = overrideValues || form.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        status: values.status,
        need_total: pageCfg.current === 1,
      };
      if (urlFilters.debtId != null && !Number.isNaN(urlFilters.debtId) && urlFilters.debtId > 0) {
        params.debt_id = urlFilters.debtId;
      }
      const keyword = (values.keyword || '').trim();
      if (keyword) params.keyword = keyword;
      const { data } = await listDebtNoticeBatches(params);
      const list = Array.isArray(data?.records) ? data.records : [];
      setRecords(list);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error(e?.message || '加载催告历史失败');
    } finally {
      setLoading(false);
    }
  }, [form, urlFilters.debtId]);

  useEffect(() => {
    // 首次进入页面：如果 URL 带了 debtNo，则预填 keyword 并立即按该值查询一次
    const initialValues = {};
    if (urlFilters.debtNo) {
      initialValues.keyword = urlFilters.debtNo;
      form.setFieldsValue(initialValues);
    }
    setPagination(prev => ({ ...prev, current: 1 }));
    if (skipNextAutoFetchRef.current) {
      skipNextAutoFetchRef.current = false;
      return;
    }
    fetchList({ current: 1, pageSize: pagination.pageSize }, Object.keys(initialValues).length ? initialValues : undefined);
  }, [fetchList, form, pagination.pageSize, urlFilters.debtNo]);

  const handleSearch = () => {
    const next = { current: 1, pageSize: pagination.pageSize, total: pagination.total };
    setPagination(next);
    fetchList(next);
  };

  const handlePaginationChange = (current, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const next = {
      current: pageSize !== pagination.pageSize ? 1 : current,
      pageSize: nextPageSize,
      total: pagination.total,
    };
    setPagination(next);
    fetchList(next);
  };

  const handleReset = () => {
    // 重置：清空表单筛选，并清除 URL 参数（避免 URL 继续影响初始筛选）
    form.resetFields();
    const next = { current: 1, pageSize: pagination.pageSize, total: 0 };
    setPagination(next);
    if (location.search) {
      skipNextAutoFetchRef.current = true;
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }
    fetchList(next);
  };

  const openNoticeFile = async (token) => {
    if (!token) return;
    try {
      const { data } = await getDebtNoticeInternalDownloadUrl(token);
      const url = String(data?.url || '');
      if (!url) throw new Error('获取下载链接失败');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      message.error(e?.message || '获取下载链接失败');
    }
  };

  const openDetail = async (batchId) => {
    try {
      setDetailLoading(true);
      const { data } = await getDebtNoticeBatchStatus(batchId);
      setDetail(data || null);
      setDetailOpen(true);
    } catch (e) {
      message.error(e?.message || '获取批次详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!detail?.batchId) return;
    await openDetail(detail.batchId);
  };

  const noticeBatchStatusLabel = (v) => {
    const s = String(v || '').toUpperCase();
    if (s === 'SENDING') return '发送中';
    if (s === 'PARTIAL_SUCCESS') return '部分成功';
    if (s === 'SUCCESS') return '成功';
    if (s === 'FAILED') return '失败';
    if (s === 'CANCELLED') return '已取消';
    return v || '-';
  };

  const noticeBatchStatusColor = (v) => {
    const s = String(v || '').toUpperCase();
    if (s === 'SUCCESS') return 'green';
    if (s === 'FAILED') return 'red';
    if (s === 'PARTIAL_SUCCESS') return 'orange';
    if (s === 'SENDING') return 'blue';
    if (s === 'CANCELLED') return 'default';
    return 'default';
  };

  const openResend = (row) => {
    setResendBatch(row || null);
    setResendSms(true);
    setResendEmail(false);
    setResendCode('');
    setResendOpen(true);
  };

  const handleSendResendCode = async () => {
    if (!resendBatch?.batchId) return;
    setResendSendingCode(true);
    try {
      // 使用专门的重发验证码接口，使用 batchId 而非 debtId
      await sendDebtNoticeResendCode(resendBatch.batchId);
      message.success('验证码已发送，请查收短信');
    } catch (e) {
      message.error(e?.message || '验证码发送失败');
    } finally {
      setResendSendingCode(false);
    }
  };

  const handleResend = async () => {
    if (!resendBatch?.batchId) return;
    if (!resendSms && !resendEmail) {
      message.warning('请至少选择一种发送方式');
      return;
    }
    const code = String(resendCode || '').trim();
    if (!code) {
      message.warning('请输入短信验证码');
      return;
    }
    setResendSending(true);
    try {
      const payload = {
        sendSms: resendSms,
        sendEmail: resendEmail,
        verificationCode: code,
      };
      const { data } = await resendDebtNoticeBatch(resendBatch.batchId, payload);
      if (data) {
        setDetail(data);
        setDetailOpen(true);
      }
      setResendOpen(false);
      message.success('已发起重发');
      fetchList(pagination);
    } catch (e) {
      message.error(e?.message || '重发失败');
    } finally {
      setResendSending(false);
    }
  };

  const openDebtDetail = (debtId) => {
    if (!debtId) return;
    navigate(`/debt-management/detail/${debtId}`);
  };

  const buildNoticeLetterShareUrl = (token) => {
    if (!token) return '';
    const origin = window.location?.origin || '';
    return `${origin}/share/notice-letter/${token}`;
  };

  const handleCopyShareUrl = async () => {
    const text = String(shareUrl || '');
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('已复制链接');
    } catch (_) {
      message.error('复制失败');
    }
  };

  const openSharePicker = async (batchId) => {
    if (!batchId) return;
    setSharePickOpen(true);
    setSharePickLoading(true);
    setSharePickBatchId(batchId);
    setSharePickRecipients([]);
    setSharePickRecipientId(null);
    try {
      const { data } = await getDebtNoticeBatchStatus(batchId);
      const list = Array.isArray(data?.recipients) ? data.recipients : [];
      if (!list.length) {
        message.warning('未找到可分享的催告函');
        setSharePickOpen(false);
        return;
      }
      setSharePickRecipients(list);
      setSharePickRecipientId(list[0]?.id || null);
    } catch (e) {
      message.error(e?.message || '获取批次详情失败');
      setSharePickOpen(false);
    } finally {
      setSharePickLoading(false);
    }
  };

  const handleGenerateShareQr = async () => {
    const rid = sharePickRecipientId;
    if (!rid) return;
    setSharePickOpen(false);
    setShareOpen(true);
    setShareLoading(true);
    setShareUrl('');
    setShareQrUrl('');
    try {
      const { data } = await createNoticeLetterShare(rid);
      const token = data?.token || '';
      const url = buildNoticeLetterShareUrl(token);
      if (!url) {
        message.error('生成分享链接失败');
        return;
      }
      const qrWidth = isMobile ? 180 : 220;
      const qr = await QRCode.toDataURL(url, { margin: 1, width: qrWidth });
      setShareUrl(url);
      setShareQrUrl(qr);
    } catch (e) {
      message.error(e?.message || '生成分享链接失败');
    } finally {
      setShareLoading(false);
    }
  };

  const columns = [
    { title: '批次ID', dataIndex: 'batchId', key: 'batchId', width: 120 },
    {
      title: '债权编号',
      dataIndex: 'debtNo',
      key: 'debtNo',
      width: 160,
      render: (v, row) => (
        v ? (
          <Button
            type="link"
            onClick={() => openDebtDetail(row.debtId)}
            className="c-btn-link-compact"
          >
            {v}
          </Button>
        ) : (
          '-'
        )
      ),
    },
    { title: '债权名称', dataIndex: 'debtTitle', key: 'debtTitle', ellipsis: true },
    {
      title: '本金金额',
      dataIndex: 'principalAmount',
      key: 'principalAmount',
      width: 120,
      align: 'right',
      className: 'u-tabular-nums',
      render: (v) => formatAmountText(v),
    },
    {
      title: '合计金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      className: 'u-tabular-nums',
      render: (v) => formatAmountText(v),
    },
    {
      title: '发生日',
      dataIndex: 'debtOccurrenceDate',
      key: 'debtOccurrenceDate',
      width: 120,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v) => {
        return <Tag color={noticeBatchStatusColor(v)}>{noticeBatchStatusLabel(v)}</Tag>;
      },
    },
    {
      title: '完成时间',
      dataIndex: 'finishedAt',
      key: 'finishedAt',
      width: 180,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : ''),
    },
    {
      title: '操作',
      key: 'action',
      width: isMobile ? 220 : 300,
      render: (_, record) => (
        <ActionCell
          record={record}
          layoutKey={isMobile ? 'mobile' : 'desktop'}
          actionWidth={isMobile ? '220px' : '300px'}
          onOpenDetail={(r) => openDetail(r?.batchId)}
          onOpenResend={(r) => openResend(r)}
          onOpenWechatShare={(r) => openSharePicker(r?.batchId)}
        />
      ),
    },
  ];

  return (
    <div className="page-container p-debt-notice-history">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">催告历史及重发</div>

        <Card className="card-compact">
          <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
          <Form.Item name="status" label="状态" initialValue="">
              <Select style={{ width: isMobile ? '100%' : 180 }} options={statusOptions} />
          </Form.Item>
          <Form.Item name="keyword" label="关键字">
              <Input placeholder="请输入债权编号或债权名称" allowClear style={{ width: isMobile ? '100%' : 260 }} />
          </Form.Item>
          <Form.Item>
              <Space wrap>
              <Button type="primary" htmlType="submit">查询</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

        <Card className="card-compact">
          <Table
            rowKey="batchId"
            loading={loading}
            dataSource={records}
            columns={columns}
            size={isMobile ? 'small' : 'middle'}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
          <div className="c-pagination-bar">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              simple={isMobile}
              showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              onChange={handlePaginationChange}
            />
          </div>
        </Card>

      <Drawer
        title="发送批次详情"
        className="notice-history-detail-drawer"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? undefined : 720}
        height={isMobile ? '85vh' : undefined}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        extra={
          detail?.batchId ? (
            <Button size="small" onClick={refreshDetail} loading={detailLoading}>
              刷新
            </Button>
          ) : null
        }
      >
        {detail?.batchId ? (
          <div className="space-y-md">
            <Card className="card-compact" size="small">
              <Space wrap>
                <Tag color="blue">{`批次ID ${detail.batchId}`}</Tag>
                <Tag color={noticeBatchStatusColor(detail.status)}>
                  {noticeBatchStatusLabel(detail.status)}
                </Tag>
              </Space>
            </Card>

            <Card className="card-compact" size="small">
              <div className="notice-history-detail-section-title">收件人</div>
              <Table
                size="small"
                rowKey={(r) => String(r?.id)}
                dataSource={Array.isArray(detail?.recipients) ? detail.recipients : []}
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: '债务人', dataIndex: 'receiverName', key: 'receiverName', width: 180, render: (v) => v || '-' },
                  { title: '手机号', dataIndex: 'receiverPhone', key: 'receiverPhone', width: 150, render: (v) => v || '-' },
                  { title: '邮箱', dataIndex: 'receiverEmail', key: 'receiverEmail', width: 200, render: (v) => v || '-' },
                  {
                    title: '催告函',
                    dataIndex: 'downloadToken',
                    key: 'downloadToken',
                    width: 120,
                    render: (v) => (
                      v ? (
                        <Button type="link" className="c-btn-link-compact" onClick={() => openNoticeFile(v)}>查看</Button>
                      ) : '-'
                    ),
                  },
                ]}
              />
            </Card>

            <Card className="card-compact" size="small">
              <div className="notice-history-detail-section-title">附件</div>
              <Table
                size="small"
                rowKey={(r) => String(r?.id)}
                dataSource={Array.isArray(detail?.attachments) ? detail.attachments : []}
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: '附件名', dataIndex: 'fileName', key: 'fileName', render: (v) => v || '-' },
                  {
                    title: '操作',
                    key: 'op',
                    width: 120,
                    render: (_, row) => (
                      row?.token ? (
                        <Button type="link" className="c-btn-link-compact" onClick={() => openNoticeFile(row.token)}>查看</Button>
                      ) : '-'
                    ),
                  },
                ]}
              />
            </Card>

            <Card className="card-compact" size="small">
              <div className="notice-history-detail-section-title">发送任务</div>
              <Table
                size="small"
                rowKey={(r) => String(r?.id)}
                dataSource={Array.isArray(detail?.tasks) ? detail.tasks : []}
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: '债务人', dataIndex: 'debtorName', key: 'debtorName', width: 180, render: (v) => v || '-' },
                  {
                    title: '渠道',
                    dataIndex: 'channel',
                    key: 'channel',
                    width: 90,
                    render: (v) => {
                      if (v === 'SMS') return '短信';
                      if (v === 'EMAIL') return '邮件';
                      return v || '-';
                    },
                  },
                  { title: '发送目标', dataIndex: 'sendTo', key: 'sendTo', width: 200, render: (v) => v || '-' },
                  { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => noticeBatchStatusLabel(v) },
                  { title: '错误', dataIndex: 'errorMessage', key: 'errorMessage', render: (v) => v || '-' },
                ]}
              />
            </Card>
          </div>
        ) : null}
      </Drawer>

      <Drawer
        title="重发催告"
        className="notice-history-resend-drawer"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? undefined : 400}
        height={isMobile ? '70vh' : undefined}
        open={resendOpen}
        onClose={() => setResendOpen(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <div>批次ID：{resendBatch?.batchId}</div>
            <div>债权名称：{resendBatch?.debtTitle || '-'}</div>
          </div>
          <Space>
            <Checkbox checked={resendSms} onChange={(e) => setResendSms(e.target.checked)}>
              短信
            </Checkbox>
            <Checkbox checked={resendEmail} onChange={(e) => setResendEmail(e.target.checked)}>
              邮件
            </Checkbox>
          </Space>
          <Space wrap>
            <Button onClick={handleSendResendCode} loading={resendSendingCode} disabled={!resendBatch?.batchId}>
              获取短信验证码
            </Button>
            <Input
              placeholder="请输入短信验证码"
              value={resendCode}
              onChange={(e) => setResendCode(e.target.value)}
              style={{ width: isMobile ? '100%' : 160 }}
            />
          </Space>
          <Space wrap>
            <Button type="primary" onClick={handleResend} loading={resendSending}>
              确认重发
            </Button>
            <Button onClick={() => setResendOpen(false)}>取消</Button>
          </Space>
        </Space>
      </Drawer>

      <Modal
        title="选择要分享的催告函"
        open={sharePickOpen}
        onCancel={() => setSharePickOpen(false)}
        okText="生成二维码"
        onOk={handleGenerateShareQr}
        okButtonProps={{ disabled: !sharePickRecipientId, loading: sharePickLoading }}
        cancelButtonProps={{ disabled: sharePickLoading }}
      >
        <Text type="secondary">{`批次ID：${sharePickBatchId || '-'}`}</Text>
        <Radio.Group
          style={{ width: '100%', marginTop: 8 }}
          value={sharePickRecipientId}
          onChange={(e) => setSharePickRecipientId(e.target.value)}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {(sharePickRecipients || []).map((r) => (
              <Radio key={r?.id} value={r?.id}>
                {`${r?.receiverName || r?.debtorKey || '-'} ${r?.receiverPhone ? `(${r.receiverPhone})` : ''}`}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title="微信分享"
        open={shareOpen}
        onCancel={() => setShareOpen(false)}
        width={isMobile ? 360 : 420}
        footer={null}
        wrapClassName="notice-history-share-modal"
      >
        <div className="notice-history-share-body">
          <div className="notice-history-share-qr">
            {shareQrUrl ? (
              <img src={shareQrUrl} alt="qrcode" />
            ) : (
              <div className="notice-history-share-loading">生成中...</div>
            )}
          </div>
          <div className="notice-history-share-tip">二维码可微信扫码转发好友/朋友圈</div>
          <div className="notice-history-share-actions">
            <Button onClick={() => setShareOpen(false)} disabled={shareLoading}>取消</Button>
            <Button type="primary" onClick={handleCopyShareUrl} disabled={!shareUrl} loading={shareLoading}>复制链接</Button>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default NoticeHistoryPage;
